import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, map, take, takeWhile } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subscription, timer } from 'rxjs';

import { isEmpty } from 'lodash-es';
import { NgxPermissionsService } from 'ngx-permissions';

import { Blocker } from '../../../../core/models';
import { TemplateType } from '../../../../core/enums';
import { currentUser, User } from '../../../../core/auth';
import { AppState } from '../../../../core/reducers';
import { AuthService, UserService } from '../../../../core/auth/_services';
import { TaskService } from '../../../../core/timeline';
import { OfficesService } from '../../../../core/management';
import { ContactRelationshipsService } from '../../../../core/lists';
import { TaskAiFields } from '../../../../core/lists/_models/task-ai-fields';
import { TemplateService } from '../../../../core/lists/_services/template.service';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../core/_base/crud';
import { TagService } from '../../../../core/services/tag-service.service';
import { BlockerService } from '../../../../core/services/blocker.service';
import { FilteredViewService } from '../../../../core/services/filtered-view.service';
import { ProcessNavigatorService } from '../../../../core/services/process-navigator.service';
import { Form, FormTransactionLink } from '../../forms-templates/models';
import { FormService } from '../../forms-templates/services';
import { ShareDocumentComponent } from '../../documents-management/components';
import { CommentAction } from '../../../partials/content/general/comments/comments.component';
import { GeneralAutocompleteComponent } from '../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { ProcessNavViewDialogComponent } from '../../../partials/content/general/process-nav-view-dialog/process-nav-view-dialog.component';
import { TifViewDialogComponent } from '../../apps/events/inbox/tif-view-dialog/tif-view-dialog.component';
import { ChooseTemplateDialogComponent } from '../choose-template/choose-template.dialog.component';
import { ShareTaskAttachmentComponent } from '../share-task-attachment/share-task-attachment.component';
import { WarnCreateDuplicateTaskComponent } from '../warn-create-duplicate-task/warn-create-duplicate-task.component';
import {
  Task,
  TaskAttachment,
  TaskComment,
  TaskEntity,
  TaskStep,
  TaskTag,
  TaskTemplate,
  TaskTemplateStep,
} from '../models';
import {
  TaskAttachmentIcon,
  TaskDefaultView,
  TaskDueDateStatus,
  TaskDueDateStatusClass,
  TaskEntityType,
  TaskMode,
  TaskPriority,
  TaskPriorityClass,
  TaskStatus,
  TaskStatusClass,
  TaskVariableAreaView,
} from '../enums';
import { FindValueInJsonByKeyPipe } from '../../../../core/_base/layout/pipes/find-value-in-json-by-key.pipe';
import { FollowUpDialogComponent } from '../../follow-up/follow-up-dialog/follow-up-dialog.component';
import { TaskStateService } from 'src/app/core/_base/layout/services/task-state.service';
import { ProcessPaymentComponent } from 'src/app/core/bridge-pay/components/process-payment/process-payment.component';
import { PaypointService } from '../../../../core/bridge-pay/services/paypoint.service';
import { PaymentProcessService } from '../../../../core/bridge-pay/services/payment-process.service';
import { GlobalSettingsService } from '../../../../core/services/global-settings.service';
import { StringUtilsService } from '../../../../core/services/string-utils.service';

const URL_PATTERN =
  'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9áéíóúüÁÉÍÓÚÜñÑ()@:%_+.~#?&\\/=]*)';
// Timeline starts agent work from its outbox every 5 seconds.
const AGENT_RUN_CHECK_MS = 5000;
const AGENT_RUN_CHECK_ATTEMPTS = 3;

@Component({
  selector: 'kt-create-task',
  templateUrl: './create-task.component.html',
  styleUrls: ['./create-task.component.scss'],
})
export class CreateTaskComponent implements OnInit, OnDestroy {
  // for HTML template use
  VarAreaView = TaskVariableAreaView;
  Status = TaskStatus;
  private subscriptions = new Subscription();
  private taskReload = Subscription.EMPTY;
  private agentRunWait = Subscription.EMPTY;
  currentUser: User;
  currentUserTeamsIds: number[];
  isCreate: boolean;
  canEdit: boolean;
  submitted = false;
  form: FormGroup;
  forms: FormGroup[];
  currentOrder: number;
  activeOrder: number;
  varAreaViews: TaskVariableAreaView[];
  activeViewData: { view: TaskVariableAreaView; disabled: boolean; content: any };
  showTitleInput: boolean;
  showBlockersSelect: boolean;
  templateBlockers: Blocker[];
  priorities: TaskPriority[] = Object.values(TaskPriority);
  statuses: TaskStatus[] = Object.values(TaskStatus);
  fetchUser = false;
  getUserAutocomplete$: Observable<User>;
  entities$ = new BehaviorSubject<Partial<TaskEntity>[]>([]);
  getEntitiesAutocomplete$: Observable<TaskEntity[]>;
  entityTypes: TaskEntityType[] = Object.values(TaskEntityType);
  dueDateStatus: TaskDueDateStatus;
  showLink: boolean;
  showChecklistError = false;
  actionMapperEntityError = { show: false, message: '' };
  actionMapperFormError = { show: false, message: '' };
  getOfficeAutocomplete$ = this.officesService.searchOffices.bind(this.officesService);
  prevAttachments: TaskAttachment[];
  newAttachments: File[];
  relatedTemplates: TaskTemplate[];
  comments: TaskComment[];
  getTagsAutocomplete$ = this.tagService.searchTags.bind(
    this.tagService,
    'Communication',
    'Labels',
  );
  @ViewChild('viewSelect', { static: true }) viewSelect: MatSelect;
  @ViewChild('assigneeAutocomplete', { static: true })
  assigneeAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('followerAutocomplete', { static: true })
  followerAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('entityTypeSelect', { static: true }) entityTypeSelect: MatSelect;
  @ViewChild('entitiesAutocomplete', { static: true })
  entitiesAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('officeAutocomplete', { static: true })
  officeAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('tagsAutocomplete', { static: true }) tagsAutocomplete: GeneralAutocompleteComponent;
  showFormioError = false;
  formStepValidComplete = false;
  isLoading = true;
  enableAsyncTaskDetailsLoading = false;
  reinitRequired = true;
  taskAllowPayment = false;
  paidSuccessfully = false;
  templateAi: TaskAiFields | null = null;
  private aiLoad = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      enableAsyncTaskDetailsLoading: boolean;
      mode: TaskMode;
      fromCalendar?: boolean;
      templateSteps?: TaskTemplateStep[];
      task?: Task;
      stepId?: number;
      entities?: TaskEntity[];
      templateId?: number;
    },
    private dialogRef: MatDialogRef<CreateTaskComponent>,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private datePipe: DatePipe,
    private findValueInJsonByKeyPipe: FindValueInJsonByKeyPipe,
    private translate: TranslateService,
    private ngxPermissions: NgxPermissionsService,
    private layoutUtils: LayoutUtilsService,
    private blockerService: BlockerService,
    private processNavService: ProcessNavigatorService,
    private userService: UserService,
    private taskService: TaskService,
    private templateService: TemplateService,
    private officesService: OfficesService,
    private tagService: TagService,
    private formService: FormService,
    private filteredViewService: FilteredViewService,
    private relationshipsService: ContactRelationshipsService,
    private permissionsService: NgxPermissionsService,
    private taskStateService: TaskStateService,
    private paypointService: PaypointService,
    private processPaymentService: PaymentProcessService,
    private authService: AuthService,
    private globalSettingsService: GlobalSettingsService,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit(): void {
    this.initializeTaskView();

    // Ensure payment flags are always evaluated, regardless of other errors
    this.initializePaymentFlags();
  }

  private initializePaymentFlags(): void {
    try {
      // Initialize payment flags in isolated method to ensure execution
      const isCreate = this.data.mode === TaskMode.CREATE;

      if (isCreate) {
        if (this.data.templateId) {
          this.templateService.getTemplateById(this.data.templateId).subscribe(
            (res: any) => {
              try {
                if (!res.data) return;
                this.taskAllowPayment = res.data[0].allow_payment;
                this.taskStateService.setSelectedTask({
                  ...this.data.task,
                  allow_payment: this.taskAllowPayment,
                });
              } catch {
                this.taskAllowPayment = false;
              }
            },
            () => {
              this.taskAllowPayment = false;
            },
          );
        } else {
          // No template, set default
          this.taskAllowPayment = false;
        }
      } else {
        // For existing tasks, get payment flag from task data
        this.taskAllowPayment =
          this.data.task?.steps[this.activeOrder - 1]?.task_template?.allow_payment ?? false;
      }
    } catch (error) {
      // Ensure flag always has a value
      this.taskAllowPayment = false;
    }
  }

  private subscribeToTaskState(): void {
    const loadingSubscription = this.taskStateService.isLoading$.subscribe((loading) => {
      this.isLoading = loading;
    });
    const taskSubscription = this.taskStateService.selectedTask$.subscribe((task) => {
      if (task) {
        this.data.task = task;
        // Preserve payment flag when task is updated from state service
        if (task.allow_payment !== undefined) {
          this.taskAllowPayment = task.allow_payment;
        }
        this.initializeTaskView();
      }
    });
    this.subscriptions.add(loadingSubscription);
    this.subscriptions.add(taskSubscription);
  }

  private initializeTaskView(): void {
    this.isCreate = this.data.mode === TaskMode.CREATE;
    const steps = this.isCreate ? this.data.templateSteps : this.data.task?.steps;

    steps?.sort((a, b) => (a.order < b.order ? -1 : 1));

    this.currentOrder =
      (steps as TaskStep[])?.find(
        (s) => s.progress !== TaskStatus.CANCELED && s.progress !== TaskStatus.DONE,
      )?.order || 1;
    this.activeOrder = null;
    this.entities$.next((this.isCreate ? this.data.entities : this.data.task?.task_entities) || []);
    this.prevAttachments = this.data.task?.task_attachments || [];
    this.newAttachments = [];
    this.comments = this.data.task?.task_comments || [];
    this.templateBlockers = [];

    // Re-initialize payment flags when task view is initialized (especially important after task state updates)
    if (!this.isCreate && this.data.task?.allow_payment !== undefined) {
      this.taskAllowPayment = this.data.task.allow_payment;
    }

    this.subscriptions.add(
      this.store.pipe(select(currentUser)).subscribe((user) => {
        this.currentUser = user;
        this.forms = steps?.map((s) => this.initForm(s)) || [this.initForm()];

        // Check payment status once currentUser is available
        if (user && !this.isCreate && this.taskAllowPayment && this.data?.task?.id) {
          this.checkPaymentStatus();
        }
      }),
    );
    this.subscriptions.add(
      this.store.pipe(select(currentUser)).subscribe((user) => {
        this.currentUserTeamsIds = user.categories?.map((c: string | number) => +c);

        const activeOrder = this.data.stepId
          ? (steps as TaskStep[])?.find((s) => s.id === this.data.stepId)?.order
          : this.currentOrder;

        this.activateStep(null, activeOrder);

        if (this.entities$.value.length) {
          this.taskService
            .getEntities(this.entities$.value.map((e) => e.entity_id))
            .pipe(take(1))
            .subscribe((res) => {
              if (res?.data?.length) {
                const entities = res.data.map(
                  (e: { entity_type: TaskEntityType; id: any; entity_id: any }) => {
                    if (!this.isCreate && e.entity_type === TaskEntityType.PATIENT)
                      this.filteredViewService
                        .createRecentSession({
                          entity_id: e.id,
                          access_from: 'Task List',
                          entity_type: TaskEntityType.PATIENT,
                        })
                        .pipe(take(1))
                        .subscribe();

                    return { ...e, external_id: e.entity_id, entity_id: e.id };
                  },
                );

                this.entities$.next(entities);
                this.entitiesAutocomplete.setValue(entities);
                this.entitiesAutocomplete.updateValueAndValidity();
              }
            });
        }
      }),
    );

    setTimeout(() => {
      this.tagsAutocomplete.setValue(this.data.task?.task_tags);
      this.tagsAutocomplete.updateValueAndValidity();
    });

    this.taskService.checkFormValidity().subscribe((result) => {
      this.formStepValidComplete = result;
    });
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.taskReload.unsubscribe();
    this.agentRunWait.unsubscribe();
    this.taskStateService.resetSelectedTask();
  }

  initForm(step?: TaskTemplateStep | TaskStep): FormGroup {
    return this.formBuilder.group({
      id: [this.isCreate ? null : (step as TaskStep)?.id],
      defaultView: [
        (this.isCreate ? step?.task_template.default_view : (step as TaskStep)?.default_view) ||
          TaskDefaultView.DETAILED,
      ],
      priority: [
        this.isCreate
          ? step?.task_template.priority || TaskPriority.LOW
          : (step as TaskStep)?.priority,
        Validators.required,
      ],
      title: [
        this.isCreate ? step?.task_template.title : (step as TaskStep)?.title,
        Validators.required,
      ],
      template: [step?.task_template],
      templateResources$: [
        !this.isCreate && step?.task_template?.id
          ? this.processNavService
              .getResources(step.task_template.id, TemplateType.TASK_TEMPLATE)
              .pipe(map((r) => r?.data?.filter((s) => s.path)))
          : of(null),
      ],
      sequentialOrder: [!!step?.sequential_order],
      status: [
        this.isCreate ? TaskStatus.PLANNED : (step as TaskStep)?.progress,
        Validators.required,
      ],
      blockers: [(step as TaskStep)?.blockers],
      assignees: [
        (this.isCreate
          ? step?.task_template
            ? step.task_template.task_responsible_users
            : [{ ...this.currentUser, responsible_id: this.currentUser.id }]
          : (step as TaskStep)?.task_responsible_users) || [],
      ],
      followers: [
        (this.isCreate ? step?.task_template.task_followers : (step as TaskStep)?.task_followers) ||
          [],
      ],
      created: [(step as TaskStep)?.created ? new Date(`${(step as TaskStep).created} UTC`) : null],
      dueDate: [
        (step as TaskStep)?.due_date ? new Date(`${(step as TaskStep).due_date} UTC`) : null,
      ],
      urlResource: [
        this.isCreate ? step?.task_template.url_resource : (step as TaskStep)?.url_resource,
        Validators.pattern(URL_PATTERN),
      ],
      office: [
        (step as TaskStep)?.office_id || this.currentUser.primaryOffice,
        Validators.required,
      ],
      description: [
        this.isCreate ? step?.task_template.description : (step as TaskStep)?.description,
      ],
      checklist: [this.isCreate ? step?.task_template.check_list : (step as TaskStep)?.check_list],
      intervention: [(step as TaskStep)?.intervention],
      transactionForm: [
        this.isCreate
          ? step?.task_template.default_view === TaskDefaultView.FORM
            ? new FormTransactionLink(0, new Form(step.task_template.form_id), null)
            : null
          : (step as TaskStep)?.transaction_form,
      ],
    });
  }

  get agentRunId(): string {
    const step = this.data?.task?.steps?.[this.activeOrder - 1] as TaskStep & {
      run_id?: string;
      agent_run_id?: string;
    };
    return step?.run_id || step?.agent_run_id || '';
  }

  private loadStepAi(template?: TaskTemplate): void {
    const token = ++this.aiLoad;
    if (!this.isCreate) {
      this.templateAi = this.aiFrom(template);
      return;
    }
    const stepTemplateId = template?.id;
    const parentId = this.data.templateId || this.data.task?.task_template?.id || stepTemplateId;

    if (this.data.templateSteps?.length) {
      const match = this.data.templateSteps.find(
        (step) => step.task_template?.id === stepTemplateId,
      );
      const fromCreate = this.aiFrom(match);
      if (fromCreate) {
        this.templateAi = fromCreate;
        return;
      }
    }

    if (!parentId) {
      this.templateAi = null;
      return;
    }

    this.templateService
      .getTaskTemplateSteps(parentId)
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (token !== this.aiLoad) {
            return;
          }
          const steps = res?.data?.additional_step_template_dtos || [];
          const match = steps.find((step) => step.task_template?.id === stepTemplateId);
          const fromStep = this.aiFrom(match);
          if (fromStep) {
            this.templateAi = fromStep;
            return;
          }
          this.loadTemplateAi(stepTemplateId || parentId, token, parentId);
        },
        () => {
          if (token !== this.aiLoad) {
            return;
          }
          this.loadTemplateAi(stepTemplateId || parentId, token, parentId);
        },
      );
  }

  private loadTemplateAi(templateId: number, token: number, parentId?: number): void {
    if (!templateId) {
      this.templateAi = null;
      return;
    }
    this.templateService
      .getTemplateById(templateId)
      .pipe(take(1))
      .subscribe((res) => {
        if (token !== this.aiLoad) {
          return;
        }
        const ai = this.aiFrom(res?.data?.[0]);
        if (ai || !parentId || parentId === templateId) {
          this.templateAi = ai;
          return;
        }
        this.templateService
          .getTemplateById(parentId)
          .pipe(take(1))
          .subscribe((parent) => {
            if (token !== this.aiLoad) {
              return;
            }
            this.templateAi = this.aiFrom(parent?.data?.[0]);
          });
      });
  }

  private aiFrom(source?: {
    id?: number;
    objective?: string;
    critical_data?: string;
    data_sources?: string;
    authorized_communication_methods?: string[] | string;
  } | null): TaskAiFields | null {
    if (!source) {
      return null;
    }
    const methods = source.authorized_communication_methods;
    const ai: TaskAiFields = {
      objective: source.objective || '',
      critical_data: source.critical_data || '',
      data_sources: source.data_sources || '',
      authorized_communication_methods: Array.isArray(methods)
        ? (methods as TaskAiFields['authorized_communication_methods'])
        : [],
    };
    const hasText = !!(ai.objective || ai.critical_data || ai.data_sources);
    return hasText || ai.authorized_communication_methods.length ? ai : null;
  }

  activateStep(event: Event, order: number): void {
    event?.stopPropagation();

    if (order === this.activeOrder) return;

    this.fetchUser = false;
    this.activeOrder = order;
    this.form = this.forms[order - 1];
    this.showTitleInput = !this.form.get('title').value;
    this.relatedTemplates = [];

    const templateTeamId = this.form.get('template').value?.category_id;
    const savedStatus = this.data.task?.steps?.find(
      (s) => s.id === this.form.get('id').value,
    )?.progress;

    this.canEdit =
      this.isCreate ||
      (this.data.mode === TaskMode.UPDATE &&
        !!this.permissionsService.getPermission('canEditTask') &&
        savedStatus !== TaskStatus.CANCELED &&
        savedStatus !== TaskStatus.DONE &&
        (order === this.currentOrder || !this.form.get('sequentialOrder').value) &&
        (templateTeamId ? this.currentUserTeamsIds?.includes(templateTeamId) : true));

    if (!this.canEdit)
      Object.keys(this.form.controls).forEach((key) => this.form.get(key).disable());

    const template = this.form.get('template').value;
    this.loadStepAi(template);
    const defaultView = this.form.get('defaultView').value;
    const assigneesCtrl = this.form.get('assignees');
    const followersCtrl = this.form.get('followers');
    const office = this.form.get('office').value;
    const status = this.form.get('status').value;

    this.getUserAutocomplete$ = this.userService.searchUser.bind(
      this.userService,
      template?.category_id,
    );
    this.varAreaViews = [TaskVariableAreaView.ACTION_EXECUTION_LOGS, TaskVariableAreaView.ACTIVITY_HISTORY, TaskVariableAreaView.COMMENTS];

    if (defaultView === TaskDefaultView.DETAILED) {
      this.viewSelect.value = TaskVariableAreaView.COMMENTS;
      this.activateView(TaskVariableAreaView.COMMENTS);
    } else {
      this.varAreaViews.unshift(defaultView);

      this.viewSelect.value = defaultView;
      this.activateView(defaultView);
    }

    if (!!assigneesCtrl.value?.length || !!followersCtrl.value?.length) {
      const assigneesIds = assigneesCtrl.value.map(
        (a: { responsible_id: string | number }) => +a.responsible_id,
      );
      const followersIds = followersCtrl.value.map(
        (f: { follower_id: string | number }) => +f.follower_id,
      );

      this.taskService
        .getUsersbyId([...new Set([...assigneesIds, ...followersIds])])
        .pipe(take(1))
        .subscribe((res) => {
          if (res?.pageOfItems?.length) {
            assigneesCtrl.setValue(
              res.pageOfItems
                .filter((i: { id: any }) => assigneesIds.includes(i.id))
                .map((i: { id: any }) => ({ ...i, responsible_id: i.id })),
            );
            followersCtrl.setValue(
              res.pageOfItems
                .filter((i: { id: any }) => followersIds.includes(i.id))
                .map((i: { id: any }) => ({ ...i, follower_id: i.id })),
            );
          }
        });
    }

    this.handleDueDateChange(this.form.get('dueDate').value);

    this.showLink = !!this.form.get('urlResource').value;

    if (office) {
      if (typeof office === 'object') {
        this.officeAutocomplete.setValue(office);
        this.officeAutocomplete.updateValueAndValidity();
      } else
        this.officesService
          .getOfficeById(office)
          .pipe(take(1))
          .subscribe((res) => {
            if (res) {
              this.officeAutocomplete.setValue(res);
              this.officeAutocomplete.updateValueAndValidity();
            }
          });
    }

    if (!template) {
      this.showBlockersSelect = false;

      return;
    }

    this.blockerService
      .getBlockers(template.id, TemplateType.TASK_TEMPLATE)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res?.length) return;

        this.templateBlockers = res;
        this.showBlockersSelect = status === TaskStatus.WAITING || status === TaskStatus.ON_HOLD;
      });

    this.templateService
      .getRelatedTaskTemplates(template.id)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res?.data?.task_templates?.length) return;

        this.relatedTemplates = res.data.task_templates;
      });
    this.initializePaymentFlags();
    this.checkPaymentStatus();
  }

  activateView(view: TaskVariableAreaView): void {
    let content: {
      transactionForm?: any;
      entities$?: BehaviorSubject<Partial<TaskEntity>[]>;
      taskId?: number;
      templateId?: any;
      comments?: TaskComment[];
      stepId?: any;
    };
    const formVal = this.form.value;

    switch (view) {
      case TaskVariableAreaView.CHECK_LIST:
        content = formVal.checklist;
        break;

      case TaskVariableAreaView.INTERVENTION:
        content = formVal.intervention;
        break;

      case TaskVariableAreaView.FORM:
        content = { transactionForm: formVal.transactionForm, entities$: this.entities$ };
        break;

      case TaskVariableAreaView.COMMENTS:
        content = {
          taskId: this.data.task?.id,
          templateId: formVal.template?.id,
          comments: [...this.comments],
        };
        break;

      case TaskVariableAreaView.ACTIVITY_HISTORY:
        content = { taskId: this.data.task?.id, stepId: formVal.id };
        break;

      case TaskVariableAreaView.ACTION_EXECUTION_LOGS:
        content = { stepId: formVal.id };
        break;

      default:
        content = null;
    }

    this.activeViewData = { view, disabled: !this.canEdit, content };
  }

  handleVarAreaInputChange(data: { view: TaskVariableAreaView; content: any }): void {
    const statusCtrl = this.form.get('status');

    switch (data.view) {
      case TaskVariableAreaView.CHECK_LIST:
        this.form.get('checklist').setValue(data.content);

        if (data.content?.every((i: { completed: any }) => i.completed)) {
          statusCtrl.setValue(TaskStatus.DONE);
        } else if (
          statusCtrl.value === TaskStatus.DONE ||
          (statusCtrl.value === TaskStatus.PLANNED &&
            data.content?.some((i: { completed: any }) => i.completed))
        ) {
          statusCtrl.setValue(TaskStatus.IN_PROGRESS);
        }

        break;

      case TaskVariableAreaView.INTERVENTION:
        this.form.get('intervention').setValue(data.content);

        if (
          !!data.content?.acceptance_result &&
          !!data.content?.drp.outcome &&
          !!data.content?.drp.outcome_note
        ) {
          statusCtrl.setValue(TaskStatus.DONE);
        } else if (statusCtrl.value === TaskStatus.DONE) {
          statusCtrl.setValue(TaskStatus.IN_PROGRESS);
        }

        break;

      case TaskVariableAreaView.FORM:
        this.form.get('transactionForm').setValue(data.content);

        break;

      case TaskVariableAreaView.COMMENTS:
        if (statusCtrl.value === TaskStatus.PLANNED) statusCtrl.setValue(TaskStatus.IN_PROGRESS);

        switch (data.content.action) {
          case CommentAction.CREATE:
            this.comments = [data.content.comment, ...this.comments];
            break;

          case CommentAction.UPDATE:
            this.comments = this.comments.map((c) =>
              c.id === data.content.comment.id ? data.content.comment : c,
            );
            break;

          case CommentAction.DELETE:
            this.comments = this.comments.filter((c) => c.id !== data.content.comment.id);
            break;
        }

        break;

      default:
    }
  }

  getPriorityClass(type: 'BACKGROUND' | 'SVG', priority?: TaskPriority): string {
    const priorityClass = TaskPriorityClass[priority || this.form.get('priority').value];

    return type === 'SVG' ? `svg-icon-${priorityClass}` : `bg-light-${priorityClass}`;
  }

  openSelectMenu(event: Event, elementRef: MatSelect): void {
    event.stopPropagation();

    elementRef.open();
  }

  toggleTitle(event: Event): void {
    event.stopPropagation();

    if (!this.canEdit) return;

    this.showTitleInput = !this.showTitleInput;
  }

  openProcessNav(event: Event): void {
    event.stopPropagation();

    const formVal = this.form.value;

    this.dialog.open(ProcessNavViewDialogComponent, {
      width: '700px',
      disableClose: true,
      closeOnNavigation: true,
      data: {
        templateType: TemplateType.TASK_TEMPLATE,
        templateName: formVal.task_template?.name,
        transactionId: formVal.id,
        resources$: formVal.templateResources$,
      },
    });
  }

  canCancelTask(): boolean {
    return !!this.ngxPermissions.getPermission('canCancelTask');
  }

  getStatusClass(status?: TaskStatus): string {
    const statusClass = TaskStatusClass[status || this.form.get('status').value];

    return `label-light-${statusClass}`;
  }

  handleStatusChange(status: TaskStatus): void {
    this.showBlockersSelect =
      !!this.templateBlockers.length &&
      (status === TaskStatus.WAITING || status === TaskStatus.ON_HOLD);
  }

  getStepStatusClass(status: TaskStatus, isCurrent: boolean): string {
    const statusClass = TaskStatusClass[status];

    return `border-${statusClass} bg-${isCurrent ? '' : 'light-'}${statusClass} text-${
      isCurrent ? 'white' : statusClass
    }`;
  }

  compareBlockersFn(x: Blocker, y: Blocker): boolean {
    return x?.id === y?.id;
  }

  handleBlockersChange(): void {
    if (this.form.get('blockers').value?.length) return;

    this.form.get('status').setValue(TaskStatus.IN_PROGRESS);
    this.showBlockersSelect = false;
  }

  formatUserOption(user: User): string {
    return user?.id
      ? `${user.fullName}<small class="text-muted ml-2">(${user.name || ''})</small>`
      : '';
  }

  pickup(event: Event): void {
    event.stopPropagation();

    let warning: string;
    const assigneesCtrl = this.form.get('assignees');
    const templateTeamId = this.form.get('template').value?.category_id;

    if (
      assigneesCtrl.value.find(
        (a: { responsible_id: number }) => a.responsible_id === this.currentUser.id,
      )
    )
      return;
    if (!!templateTeamId && !this.currentUserTeamsIds?.includes(templateTeamId))
      warning = 'You are trying to pick up a Task related to another Team!';
    if (this.form.get('status').value !== TaskStatus.PLANNED)
      warning = 'You are trying to pick up a Task not in Planned status!';
    if (warning) {
      const description = `
        <div style="font-size: 5rem !important" class="d-flex flex-center text-warning font-weight-boldest mb-5">
          <span class="flaticon-warning svg-icon"></span>
        </div>
        <p class="text-dark text-center font-size-lg font-weight-bold">${warning}</p>
      `;

      this.layoutUtils.generalDialog('', description, '', '', 'OK', false);

      return;
    }

    assigneesCtrl.setValue([
      ...assigneesCtrl.value,
      { ...this.currentUser, responsible_id: this.currentUser.id },
    ]);
  }

  handleAssigneeChange(assignee: User): void {
    if (!assignee) return;

    const assigneesCtrl = this.form.get('assignees');

    this.fetchUser = true;
    this.assigneeAutocomplete.setValue(null);
    this.assigneeAutocomplete.updateValueAndValidity();

    if (
      assigneesCtrl.value.find((a: { responsible_id: number }) => a.responsible_id === assignee.id)
    )
      return;

    assigneesCtrl.setValue([...assigneesCtrl.value, { ...assignee, responsible_id: assignee.id }]);
  }

  getUser$(userId: number): Observable<User> {
    return userId
      ? this.taskService.getUsersbyId([userId]).pipe(map((res: any) => res?.pageOfItems[0]))
      : of(null);
  }

  unassign(event: Event, assignee: { responsible_id: any }): void {
    event.stopPropagation();

    const assigneesCtrl = this.form.get('assignees');

    assigneesCtrl.setValue(
      assigneesCtrl.value.filter(
        (a: { responsible_id: any }) => a.responsible_id !== assignee.responsible_id,
      ),
    );
  }

  handleFollowerChange(follower: User): void {
    if (!follower) return;

    const followersCtrl = this.form.get('followers');

    this.fetchUser = true;
    this.followerAutocomplete.setValue(null);
    this.followerAutocomplete.updateValueAndValidity();

    if (followersCtrl.value.find((a: { follower_id: number }) => a.follower_id === follower.id))
      return;

    followersCtrl.setValue([...followersCtrl.value, { ...follower, follower_id: follower.id }]);
  }

  toggleDropdownMenu(event: Event, elementRef: HTMLElement): void {
    event?.stopPropagation();

    elementRef.classList.contains('show')
      ? elementRef.classList.remove('show')
      : elementRef.classList.add('show');
  }

  unfollow(event: Event, follower: { follower_id: any }): void {
    event.stopPropagation();

    const followersCtrl = this.form.get('followers');

    followersCtrl.setValue(
      followersCtrl.value.filter(
        (f: { follower_id: any }) => f.follower_id !== follower.follower_id,
      ),
    );
  }

  handleEntityTypeChange(entityType: TaskEntityType): void {
    if (!entityType) return;

    this.getEntitiesAutocomplete$ = this.relationshipsService.findEntities.bind(
      this.relationshipsService,
      {
        entityTypes: [entityType],
      },
    );
  }

  formatNewEntity(entity: TaskEntity): TaskEntity {
    return entity;
  }

  handleEntities(event: Event, elementRef: HTMLElement): void {
    event.stopPropagation();

    const hasActiveFormViews = !!this.forms.filter(
      (f) =>
        f.value.defaultView === TaskDefaultView.FORM &&
        f.value.status !== TaskStatus.CANCELED &&
        f.value.status !== TaskStatus.DONE,
    ).length;
    const entities = this.entitiesAutocomplete.value.map((e: { entity_id: any; id: any }) => ({
      ...e,
      external_id: e.entity_id,
      entity_id: e.id,
    }));

    if (this.isCreate || !this.canEdit || !hasActiveFormViews) {
      this.entities$.next(entities);
      this.entityTypeSelect.value = '';
      this.toggleDropdownMenu(null, elementRef);

      return;
    }

    const description = `
      <h1 class="font-weight-boldest mb-10">
        <span class="text-danger">WARNING:</span>
        You are about to edit Entities.
      </h1>
      <p>
        <span class="font-weight-bolder">Are you sure you want to proceed?</span>
        By hitting Confirm, all active forms filled data will be cleared.
      </p>
    `;

    const dialogRef = this.layoutUtils.generalDialog('', description);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          this.entitiesAutocomplete?.setValue(this.entities$.value);
          this.entitiesAutocomplete.updateValueAndValidity();

          return;
        }

        this.forms.forEach((f) => {
          if (f.value.defaultView !== TaskDefaultView.FORM) return;
          if (f.value.status === TaskStatus.CANCELED || f.value.status === TaskStatus.DONE) return;

          const transactionFormCtrl = f.get('transactionForm');

          transactionFormCtrl.setValue({ ...transactionFormCtrl.value, input_data: '{}' });
          this.activateView(this.viewSelect?.value);
        });

        this.entities$.next(entities);
        this.entityTypeSelect.value = '';
        this.toggleDropdownMenu(null, elementRef);
      });
  }

  handleDueDateChange(dueDate: Date): void {
    this.form.get('dueDate').setValue(dueDate);

    this.dueDateStatus = ((date) => {
      if (!date) return TaskDueDateStatus.AHEAD;

      const diffDays = new Date().getDate() - date.getDate();
      const diffMonths = new Date().getMonth() - date.getMonth();
      const diffYears = new Date().getFullYear() - date.getFullYear();

      if (diffYears > 0) return TaskDueDateStatus.OVERDUE;
      if (diffYears < 0) return TaskDueDateStatus.AHEAD;
      if (diffMonths > 0) return TaskDueDateStatus.OVERDUE;
      if (diffMonths < 0) return TaskDueDateStatus.AHEAD;
      if (diffDays > 0) return TaskDueDateStatus.OVERDUE;
      if (diffDays === 0) return TaskDueDateStatus.DUE_TODAY;
      if (diffDays === -1) return TaskDueDateStatus.DUE_TOMORROW;

      return TaskDueDateStatus.AHEAD;
    })(dueDate);
  }

  getDueDateStatusClass(): string {
    return TaskDueDateStatusClass[this.dueDateStatus];
  }

  handleShowLink(event: Event, value: boolean, focusElementRef?: HTMLElement): void {
    event?.stopPropagation();

    this.showLink = value;

    if (focusElementRef) setTimeout(() => focusElementRef.focus());
  }

  openLink(event: Event, url: string): void {
    event.stopPropagation();

    if (!url) return;

    if (this.isTifUrl(url)) {
      this.dialog.open(TifViewDialogComponent, {
        data: { url },
        disableClose: true,
        closeOnNavigation: true,
      });

      return;
    }

    window.open(url, '_blank');
  }

  isTifUrl(url: string): boolean {
    if (url.toLowerCase().endsWith('.tif') || url.toLowerCase().endsWith('.tiff')) return true;

    const fileName = url.substring(url.lastIndexOf('/') + 1, url.indexOf('?'));

    return fileName.toLowerCase().endsWith('.tif') || fileName.toLowerCase().endsWith('.tiff');
  }

  formatOfficeOption(office: { id: number; name: string }): string {
    return office?.name || '';
  }

  handleOfficeChange(office: { id: number; name: string }): void {
    if (!office) return;

    this.form.get('office').setValue(office);
  }

  addAttachments(event: Event): void {
    const files: FileList = (event.target as HTMLInputElement)?.files;

    if (files?.length) this.newAttachments.push(...[].slice.call(files));
  }

  shareAttachment(event: Event, srcList: any[], idx: number): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '800px';
    dialogConfig.data = {
      taskId: this.data.task?.id,
      selectedAttachment: srcList[idx],
      attachments: srcList.filter((a) => !!a.id),
      entitiesIds: this.entities$.value.map((e) => e.entity_id),
    };

    const dialogRef = this.dialog.open(ShareTaskAttachmentComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.layoutUtils.showActionNotification(
          this.translate.instant('TASK.SHARE_ATTACHMENT.MESSAGE'),
          MessageType.Create,
          3000,
          false,
          false,
        );
      });
  }

  removeAttachment(event: Event, srcList: any[], idx: number): void {
    event.stopPropagation();

    srcList.splice(idx, 1);
  }

  getAttachmentIcon(path: string): string {
    return (
      TaskAttachmentIcon[path?.substring(path.lastIndexOf('.') + 1)?.toUpperCase()] ||
      TaskAttachmentIcon.OTHER
    );
  }

  shareDocument(event: Event): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '800px';
    dialogConfig.data = { entitiesIds: this.entities$.value.map((e) => e.entity_id) };

    const dialogRef = this.dialog.open(ShareDocumentComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.layoutUtils.showActionNotification(
          this.translate.instant('DOCS_MODULE.DOCUMENT.SHARE_MESSAGE'),
          MessageType.Create,
          3000,
          false,
          false,
        );
      });
  }

  openFollowUp(event: Event): void {
    event.stopPropagation();

    const formVal = this.form.value;
    this.dialog.open(FollowUpDialogComponent, {
      data: {
        type: 'TASK',
        title: formVal.title,
        relatedIds: this.entities$.value.map((e) => e.entity_id),
        body: { id: formVal.id },
        viewMode:
          this.form.value.status === TaskStatus.DONE ||
          this.form.value.status === TaskStatus.CANCELED,
      },
      width: '1200px',
    });
  }
  get activeStepRaw(): any {
    const steps = this.data?.task?.steps;
    if (!steps?.length || !this.activeOrder) return null;
    return steps[this.activeOrder - 1] || null;
  }

  get showProgressDetails(): boolean {
    return this.form?.get('status')?.value=== 'DONE';
  }

  get completedByType(): string | null {
    return this.activeStepRaw?.completed_by_type || null;
  }

  get isCompletedByInternal(): boolean {
    return this.completedByType === 'USER';
  }

  get isCompletedByExternal(): boolean {
    return this.completedByType === 'CONTACT';
  }

  get completedByName(): string | null {
    const s = this.activeStepRaw;
    if (!s) return null;
    return this.isCompletedByExternal
      ? s.completed_by_contact_name || null
      : s.completed_by || null;
  }

  get completedAt(): Date | null {
    const raw = this.activeStepRaw?.completed_at;
    return raw ? new Date(`${raw} UTC`) : null;
  }
  createRelatedTask(event: Event): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '30vw';
    dialogConfig.data = {
      relatedTemplates: this.relatedTemplates,
      entities: this.entities$.value.map((e) => ({ entity_id: +e.entity_id })),
    };

    this.dialog.open(ChooseTemplateDialogComponent, dialogConfig);
  }

  toggleComments(event: Event): void {
    event.stopPropagation();

    this.viewSelect.value = TaskVariableAreaView.COMMENTS;
    this.activateView(TaskVariableAreaView.COMMENTS);
  }

  formatTagOption(tag: TaskTag): string {
    return tag?.id ? tag.name : '';
  }

  formatNewTag(tag: TaskTag): TaskTag {
    return tag;
  }

  isInvalid(fieldName: string): boolean {
    const fieldCtrl = this.form.get(fieldName);

    return fieldCtrl.touched && fieldCtrl.invalid;
  }

  close(event: Event, data?: any): void {
    event?.stopPropagation();

    this.dialogRef.close(data);
  }

  reInit(task: Task): void {
    const created = this.data.mode === TaskMode.CREATE;
    this.data.mode = TaskMode.UPDATE;
    this.data.task = task;
    this.reinitRequired = !this.enableAsyncTaskDetailsLoading;
    this.taskReload.unsubscribe();
    this.subscriptions.unsubscribe();
    this.ngOnInit();
    if (created) {
      this.waitForAgentRun();
    }
  }

  reloadTask(): void {
    const taskId = this.data.task?.id;
    if (!taskId) {
      return;
    }
    const stepId = this.form.get('id').value;
    this.taskReload.unsubscribe();
    this.taskReload = this.taskService
      .getTaskV3(taskId)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res?.data) {
          return;
        }
        if (this.forms.some((f) => f.dirty) || this.newAttachments.length) {
          this.linkAgentRuns(res.data);
          this.layoutUtils.showActionNotification(
            'You have unsaved changes. Save them to load the latest step details.',
            MessageType.Read,
            3000,
            false,
            false,
          );
          return;
        }
        const openedStepId = this.data.stepId;
        if (res.data.steps?.some((s) => s.id === stepId)) {
          this.data.stepId = stepId;
        }
        this.data.task = res.data;
        this.subscriptions.unsubscribe();
        this.ngOnInit();
        // A later save must still open the task's current step.
        this.data.stepId = openedStepId;
      });
  }

  private waitForAgentRun(): void {
    const taskId = this.data.task?.id;
    if (!taskId || !this.data.task.steps?.some((s) => s.task_responsible_users?.length)) {
      return;
    }
    this.agentRunWait.unsubscribe();
    this.agentRunWait = timer(AGENT_RUN_CHECK_MS, AGENT_RUN_CHECK_MS)
      .pipe(
        takeWhile(() => !this.agentRunId),
        take(AGENT_RUN_CHECK_ATTEMPTS),
        exhaustMap(() => this.taskService.getTaskV3(taskId).pipe(catchError(() => EMPTY))),
      )
      .subscribe((res) => {
        if (res?.data) {
          this.linkAgentRuns(res.data);
        }
      });
  }

  private linkAgentRuns(task: Task): void {
    task.steps?.forEach((fresh) => {
      const step = this.data.task?.steps?.find((s) => s.id === fresh.id);
      if (step && fresh.run_id) {
        step.run_id = fresh.run_id;
      }
    });
  }

  displayMessage(
    message: string,
    messageType: MessageType,
    closeDialog: boolean,
    onCloseData?: any,
  ): void {
    setTimeout(
      () => this.layoutUtils.showActionNotification(message, messageType, 3000, false, false),
      500,
    );

    if (closeDialog) this.close(null, onCloseData);
  }

  prepareTask(): Task {
    const task = {
      id: this.data.task?.id,
      activity_id: this.data.task?.activity_id,
      activity_template_id: this.data.task?.activity_template_id,
      task_entities: this.entities$.value.map((e) => ({ entity_id: +e.entity_id })),
      task_tags: this.tagsAutocomplete.value,
      task_attachments: [
        ...this.newAttachments.map((a) => ({ id: null, name: a.name })),
        ...this.prevAttachments,
      ],
      allow_payment: this.taskAllowPayment,
      steps: this.forms.map((f, i) => ({
        id: f.value.id,
        title: f.value.title,
        order: i + 1,
        default_view: f.value.defaultView || null,
        priority: f.value.priority,
        progress: f.value.status,
        has_followup: f.value.has_followup,
        office_id: f.value.office?.id,
        description: f.value.description,
        url_resource: f.value.urlResource,
        due_date: f.value.dueDate
          ? this.datePipe.transform(f.value.dueDate, 'yyyy-MM-dd HH:mm:ss', 'UTC')
          : null,
        sequential_order: f.value.sequentialOrder,
        task_template: f.value.template,
        task_followers: f.value.followers?.map((o: { follower_id: string | number }) => ({
          follower_id: +o.follower_id,
        })),
        task_responsible_users: f.value.assignees?.map(
          (a: { responsible_id: string | number }) => ({ responsible_id: +a.responsible_id }),
        ),
        blockers: f.value.blockers,
        check_list: f.value.checklist,
        intervention: f.value.intervention,
        transaction_form: f.value.transactionForm,
      })),
    };
    for (const step of task.steps) {
      if (
        step.default_view === 'FORM' &&
        step.progress === TaskStatus.DONE &&
        this.findValueInJsonByKeyPipe.transform(step.transaction_form)
      ) {
        const e = this.findValueInJsonByKeyPipe.transform(step.transaction_form);
        if (e?.id) task.task_entities.push({ entity_id: +e.id });
      }
    }
    return task;
  }

  async canSave(): Promise<boolean> {
    let isValid = true;
    const formValue = this.form.value;
    const stepTemplateId = formValue.template?.id;
    const isDone = formValue.status === TaskStatus.DONE;
    if (this.isCreate) {
      this.forms.some((f, idx) => {
        f.markAllAsTouched();

        if (f.invalid) {
          isValid = false;
          this.activateStep(null, idx + 1);

          return true;
        }
      });
    } else {
      this.form.markAllAsTouched();
    }

    if ((this.isCreate && !isValid) || (!this.isCreate && this.form.invalid)) return false;

    if (
      formValue.defaultView === TaskDefaultView.CHECK_LIST &&
      formValue.checklist.some((i: { completed: any }) => !i.completed) &&
      isDone
    ) {
      this.showChecklistError = true;

      setTimeout(() => {
        this.form.get('status').setValue(TaskStatus.IN_PROGRESS);
        this.showChecklistError = false;
      }, 5000);

      return false;
    }

    if (formValue.defaultView === TaskDefaultView.FORM && !this.formStepValidComplete && isDone) {
      this.showFormioError = true;

      setTimeout(() => (this.showFormioError = false), 5000);

      return false;
    }

    if (!!stepTemplateId && isDone) {
      const mapperKeys = await this.templateService
        .getTaskTemplateActionMapperKeys(this.data.task?.id)
        .pipe(map((res) => res?.data || []))
        .toPromise();
      const stepMapperKeys = mapperKeys.find((k) => k.order === this.activeOrder);
      const taskEntityTypes = this.entities$.value.map((e) => e.entity_type);
      const formFieldsKeys =
        formValue.defaultView === TaskDefaultView.FORM
          ? await this.formService.getFormFieldsKeys(formValue.transactionForm?.form).toPromise()
          : [];
      const filledFormFieldsKeys = Object.entries(
        formValue.transactionForm?.input_data
          ? JSON.parse(formValue.transactionForm?.input_data)
          : {},
      )
        .filter((e) => {
          if (typeof e[1] === 'boolean' || typeof e[1] === 'number') return !!`${e[1]}`;
          if (Array.isArray(e[1])) return !!e[1].length;
          if (typeof e[1] === 'object') return !isEmpty(e[1]);

          return !!e[1];
        })
        .map((e) => e[0]);
      const unfilledFormFieldsKeys = formFieldsKeys.filter(
        (k) => !filledFormFieldsKeys.includes(k),
      );

      if (stepMapperKeys?.task_entities?.some((t) => !taskEntityTypes.includes(t))) {
        this.actionMapperEntityError = {
          show: true,
          message: `Unable to set the Task step as "DONE" unless you add entities of the following types: ${stepMapperKeys.task_entities.join(
            ', ',
          )}.`,
        };

        setTimeout(() => (this.actionMapperEntityError = { show: false, message: '' }), 5000);

        return false;
      }

      if (stepMapperKeys?.form_keys?.some((k) => !!unfilledFormFieldsKeys.includes(k))) {
        this.actionMapperFormError = {
          show: true,
          message: `Unable to set the Task step as "DONE" unless you fill out the following form fields (Note: field key provided): ${stepMapperKeys.form_keys.join(
            ', ',
          )}.`,
        };

        setTimeout(() => (this.actionMapperFormError = { show: false, message: '' }), 5000);

        return false;
      }
    }

    if (this.isCreate) {
      const templatesIds = stepTemplateId ? [stepTemplateId] : [];
      const entitiesIds = this.entities$.value.map((e) => +e.entity_id);
      const now = new Date();
      const query = new QueryParamsModel(
        {
          templatesIds,
          entitiesIds,
          createdDatetimeFrom: this.datePipe.transform(
            new Date(now.getTime() - 24 * 60 * 60 * 1000),
            'yyyy-MM-dd HH:mm:ss',
            'UTC',
          ),
          createdDatetimeTo: this.datePipe.transform(now, 'yyyy-MM-dd HH:mm:ss', 'UTC'),
        },
        'desc',
        'created',
        0,
        1000,
      );
      const matchedTasks = await this.taskService
        .getTaskPage(query)
        .pipe(map((res: any) => res?.data || []))
        .toPromise();

      if (matchedTasks?.length) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.width = '600px';
        dialogConfig.data = matchedTasks.map((t: { due_date: any }) => ({
          ...t,
          due_date: t.due_date ? new Date(`${t.due_date} UTC`) : null,
        }));

        const dialogRef = this.dialog.open(WarnCreateDuplicateTaskComponent, dialogConfig);

        return await dialogRef.afterClosed().toPromise();
      }

      return true;
    }

    return true;
  }

  async save(event: Event): Promise<void> {
    event.stopPropagation();

    const selectedBlockersWithFollowup = this.form
      .get('blockers')
      ?.value?.filter((x: { trigger_follow_up: boolean }) => x.trigger_follow_up === true);

    if (selectedBlockersWithFollowup?.length > 0) {
      this.openFollowUp(event);
    }

    if (!(await this.canSave())) return;

    let message: string;
    const messageType = this.data.task?.id ? MessageType.Update : MessageType.Create;
    this.submitted = true;

    const taskCreatUpdateAPI$: Observable<{ data: Task }> = this.data.task?.id
      ? this.taskService.updateTaskV3Step(this.form.value.id, this.prepareTask())
      : this.taskService.createTaskV3(this.prepareTask());

    taskCreatUpdateAPI$.pipe(take(1)).subscribe((res1) => {
      if (!res1?.data) {
        message = this.translate.instant('GLOBAL.UNSPECIFIED_ERROR');

        this.displayMessage(message, messageType, false);
        return;
      }
      this.globalSettingsService.loadTasks();

      const newAttachmentsUploadData = res1.data.task_attachments
        ?.filter((a) => !a.id)
        .map((a) => ({
          ...a,
          file: this.newAttachments.find(
            (n) => n.name.trim().toLowerCase() === a.name.trim().toLowerCase(),
          ),
        }));

      message = this.translate.instant(
        `TASK.${this.data.task?.id ? 'UPDATED_MESSAGE' : 'CREATED_MESSAGE'}`,
      );

      if (newAttachmentsUploadData?.length) {
        const uploadAttachmentsAPIs: Observable<HttpResponse<void>>[] =
          newAttachmentsUploadData.map((d) => this.taskService.uploadAttachment(d.url, d.file));

        forkJoin(uploadAttachmentsAPIs)
          .pipe(take(1))
          .subscribe((res2) => {
            if (res2?.some((r) => !r.ok)) {
              message = this.translate.instant(
                `TASK.${
                  this.data.task?.id
                    ? 'UPDATED_WITH_ATTACHMENT_ERROR_MESSAGE'
                    : 'CREATED_WITH_ATTACHMENT_ERROR_MESSAGE'
                }`,
              );

              this.displayMessage(message, messageType, false, res1.data);
              this.submitted = false;
              this.reInit(res1.data);
            }

            this.taskService
              .addTaskAttachments(this.data.task?.id || res1.data.id, {
                task_attachments: newAttachmentsUploadData.map((a) => ({ id: a.id, path: a.path })),
              })
              .pipe(take(1))
              .subscribe((res3) => {
                let newAttachments = res3?.data;

                if (!newAttachments?.length) {
                  newAttachments = [];
                  message = this.translate.instant(
                    `TASK.${
                      this.data.task?.id
                        ? 'UPDATED_WITH_ATTACHMENT_ERROR_MESSAGE'
                        : 'CREATED_WITH_ATTACHMENT_ERROR_MESSAGE'
                    }`,
                  );
                }

                this.displayMessage(message, messageType, false, res1.data);
                this.submitted = false;
                this.reInit({
                  ...res1.data,
                  task_attachments: [...this.data.task?.task_attachments, ...newAttachments],
                });
              });
          });
      } else {
        this.displayMessage(message, messageType, false, res1.data);
        this.submitted = false;
        this.reInit(res1.data);
      }
    });
  }

  processPayment(event: Event): void {
    event.stopPropagation();
    if (this.paidSuccessfully) return;
    const paymentDialogRef = this.dialog.open(ProcessPaymentComponent, {
      width: '500px', // Set width or other configurations
      position: { right: '0', top: '0' },
      height: '100vh',
      data: {
        companyId: this.currentUser.companyID,
        organization: this.currentUser.companyUUID,
        office: this.form.get('office').value,
        customerId: 0, // contact uuid
        taskId: this.data.task.id,
        taskEntities: this.entitiesAutocomplete.value,
      },
      disableClose: true,
    });
    paymentDialogRef.afterClosed().subscribe((res) => {
      if (res?.reTry) {
        this.processPayment(event);
        return;
      }
      this.checkPaymentStatus();
      this.processPaymentService.clearPaymentState();
    });
  }
  checkPaymentStatus() {
    // Guard against calling before currentUser is populated
    if (!this.currentUser?.companyUUID) {
      return;
    }

    if (!this.data?.task?.id) {
      return;
    }

    this.paypointService
      .getPaymentBySourceTypeAndSourceID(
        this.currentUser.companyUUID,
        'TASK',
        this.data.task.id.toString(),
      )
      .subscribe((res) => {
        if (res.data?.status !== 'Failed') this.paidSuccessfully = !!res.data;
      });
  }
  checkFollowupExist(data) {
    return !!data.task?.steps[this.activeOrder - 1]?.has_followup;
  }

  requiredFieldsEmpty() {
    return !this.form.value.title?.trim();
  }
}
